'use client';

import { useEffect, useMemo, useState } from 'react';
import { stegaClean } from '@sanity/client/stega';
import type { IBannerDesign } from '@/sanity/lib/definitions';
import {
  getBannerCountdownTarget,
  getTimeLeft,
  type TimeLeft,
} from '@/lib/bannerCountdown';

/**
 * One-second countdown for the New UI 2026 top banner / promo popup.
 *
 * Returns null until the browser has mounted (the pages are ISR-cached, so a
 * server-rendered clock would be frozen into the HTML and mismatch on
 * hydration), while the countdown window has not opened yet, and again once
 * the end date has passed — the component renders no countdown in all three
 * cases. The window is re-evaluated on every tick, so an open tab picks up
 * the start boundary and drops the countdown at the end boundary on its own.
 */
export function useBannerCountdown(
  config: IBannerDesign | null | undefined,
): TimeLeft | null {
  // Draft/Presentation mode fetches with stega enabled — invisible characters
  // inside the dates would break Date parsing (same as useLaunchEventLive).
  const cleaned = useMemo(
    () => (stegaClean(config ?? null) ?? null) as IBannerDesign | null,
    [config],
  );
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);

  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const target = getBannerCountdownTarget(cleaned, now);
      setTimeLeft(target ? getTimeLeft(target, now) : null);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [cleaned]);

  return timeLeft;
}
